/**
 * Privacy Policy for Dede Foods Limited (trading as The Grey Wolf), stored as
 * Markdown. Drafted from an audit of the actual website, backend and
 * integrations on 15 October 2026: it describes what the system does today.
 * Items that need the owner's confirmation are listed in the implementation
 * report. {{CRO}} renders CRO_NUMBER (src/data/legal.js).
 */
export const PRIVACY_VERSION = "privacy-2026-10-15";

export const privacyMarkdown = `# The Grey Wolf — Privacy Policy

**Last updated: 15 October 2026**

This Privacy Policy explains how we collect, use and protect personal information when you use https://thegreywolf.ie, book event tickets or send us an enquiry.

## 1. Who we are

The Grey Wolf is a trading name of **Dede Foods Limited**, the data controller responsible for your personal information.

Dede Foods Limited — Company Registration Number: {{CRO}}

Venue address: The Grey Wolf, Inside Lyster's Pub, 236/238 Harold's Cross Road, Dublin 6W, D6W RF44, Ireland. Our registered office details are available from the Companies Registration Office (CRO).

Email: thegreywolf2026@gmail.com

For any question about your personal information, or to exercise your rights, please email us.

## 2. What personal information we collect

**Event ticket bookings:** your name, email address, phone number (optional), the event and number of guests, your booking reference, the booking status, and the date and time you accepted our Terms & Conditions and Refund & Cancellation Policy.

**Payments:** card payments are made directly to our payment provider, SumUp. We do not receive or store your full card details. We keep a record of the payment: amount, currency, status, SumUp's checkout and transaction references, and any refund.

**Table booking requests and enquiries** (including private events and catering): your name, email address, phone number (optional), preferred date and time, number of guests, occasion and your message.

**Emails we send you:** a record of the booking and enquiry emails we send (recipient, type of email and whether it was delivered).

**Security and technical information:** to protect our booking and enquiry forms from bots and abuse, a security check by Cloudflare Turnstile processes technical information such as your IP address and browser details. For enquiries we also store a one-way, keyed code derived from your IP address (not the IP address itself) to limit repeated submissions. Our hosting and database providers keep technical logs (for example IP address, time and the page or service requested) to operate and secure their services.

We do not ask for special categories of personal information, such as health information, through our website. Please do not include allergy, health or other sensitive information in your messages. If you have allergies or dietary requirements, please speak to our staff before ordering or attending. If a message contains such information anyway, we use it only to respond to that request and keep it only as long as the enquiry itself.

## 3. How we use your information and our lawful basis

**To take and manage your booking**, process your payment, send your booking confirmation and important updates about the event, and admit you at the venue. Lawful basis: performance of a contract with you (GDPR Article 6(1)(b)).

**To answer your table request or enquiry** and arrange a booking, private event or catering with you. Lawful basis: taking steps at your request before entering into a contract (Article 6(1)(b)).

**To handle cancellations, refunds and payment problems.** Lawful basis: performance of a contract, and compliance with our legal obligations (Article 6(1)(b) and (c)).

**To keep financial and accounting records** of payments and refunds. Lawful basis: compliance with our legal obligations (Article 6(1)(c)).

**To protect our website, customers and business**, including detecting bots, preventing fraud and duplicate bookings, and keeping a record of staff actions in our booking system. Lawful basis: our legitimate interests in running a secure and reliable booking service (Article 6(1)(f)).

We do not use your information for marketing, we do not sell it, and we do not make decisions about you based solely on automated processing that have legal or similarly significant effects. The automated security check only decides whether a form submission appears to come from a person or a bot.

## 4. Who we share it with

We share personal information only with service providers who help us run the website and our bookings, and only as needed for the purposes above:

**SumUp** processes card payments and refunds. SumUp is the data controller for the payment-related personal information it processes when you pay, under its own privacy policy.

**Supabase** hosts our booking database, our staff sign-in system and our server functions, as our data processor. Our database is hosted in Ireland (EU).

**Resend** sends our booking and enquiry emails, as our data processor. Its primary processing takes place in the United States.

**Cloudflare** hosts the website, provides security services and runs the Turnstile security check, as our data processor. Cloudflare also uses Turnstile security signals as a controller to improve its bot detection.

We may also disclose information where required by law, or to protect our rights, our customers or others.

If you choose to contact us through WhatsApp or open directions in Google Maps from our website, those services process your information under their own privacy terms. Nothing is shared with them unless you use those links.

## 5. International transfers

Some of our service providers are based outside the European Economic Area (EEA) or operate global networks, so personal information may be processed outside the EEA. Their data processing terms protect these transfers as follows:

- Resend: certified under the EU–US Data Privacy Framework, with the European Commission's Standard Contractual Clauses;
- Cloudflare: certified under the EU–US Data Privacy Framework, with Standard Contractual Clauses if that certification no longer applies;
- Supabase: Standard Contractual Clauses (our database itself is hosted in Ireland).

You can ask us for more information about these safeguards.

## 6. How long we keep it

We keep personal information only for as long as we need it for the purposes described in this policy:

**Booking, payment and refund records** are kept for as long as needed to provide the booking, deal with any queries, refunds or disputes, and meet our legal obligations, including tax and accounting record-keeping requirements.

**Enquiries** are kept for as long as needed to respond to you and to arrange and provide any booking, event or catering that follows from your enquiry.

**Email delivery records** are kept for as long as the related booking or enquiry record.

**Technical and security logs** kept by our hosting providers are retained for limited periods set by those providers. For example, our email provider keeps email delivery records for 30 days. We do not keep our own request logs for the website.

When information is no longer needed, we delete it or anonymise it.

## 7. Cookies and similar technologies

We do not use advertising, analytics or tracking cookies, and we do not use third-party tracking scripts.

We use only storage that is strictly necessary for services you request:

**Booking status:** while you complete a ticket booking, your browser keeps a temporary code in its session storage so that this browser can show your booking status. It is deleted when you close the browser tab.

**Website preference:** if our WhatsApp greeting message is shown, your browser remembers for the current session that it has already been shown, so it does not appear again.

**Security check:** on our booking and enquiry forms, Cloudflare Turnstile collects technical signals that are strictly necessary to detect and block bots, as described in Cloudflare's Turnstile privacy information.

**Secure payment:** when you pay, the payment form is provided by SumUp, which may use technologies strictly necessary for secure payment processing and fraud prevention.

Because we only use strictly necessary technologies, we do not ask for cookie consent. If we ever introduce optional cookies, we will ask for your consent first.

## 8. Your rights

Under data protection law you have the right to:

- access the personal information we hold about you;
- ask us to correct inaccurate or incomplete information;
- ask us to delete your information;
- ask us to restrict how we use your information;
- object to our use of your information where we rely on our legitimate interests;
- receive information you provided to us in a portable format, where processing is based on our contract with you.

To exercise any of these rights, email thegreywolf2026@gmail.com. We may need to confirm your identity before acting on a request. We will respond within one month, as required by law. Some rights are limited, for example where we must keep records to meet a legal obligation.

## 9. Complaints

If you have concerns about how we use your personal information, please contact us first so we can try to resolve them.

You also have the right to lodge a complaint with the Data Protection Commission, Ireland's data protection supervisory authority: https://www.dataprotection.ie

## 10. Security

We use appropriate technical and organisational measures to protect personal information, including encrypted connections (HTTPS), access controls that limit access to authorised staff, and multi-factor authentication for staff accounts. Card payments are handled by SumUp rather than on our website.

No method of transmission or storage is completely secure, but we work to protect your information and to respond promptly to any security issue.

## 11. Changes to this policy

We may update this policy when our services or legal requirements change. The current version is always available at https://thegreywolf.ie/privacy, with the date it was last updated.

## 12. Contact

**The Grey Wolf — Dede Foods Limited**

Dede Foods Limited — Company Registration Number: {{CRO}}

The Grey Wolf
Inside Lyster's Pub
236/238 Harold's Cross Road
Dublin 6W, D6W RF44
Ireland

thegreywolf2026@gmail.com

https://thegreywolf.ie`;
